import { useState } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { CollapseCard } from '@components/card/collapse-card';
import { CodeBlock } from '@components/code-block';
import { createForm, useCreateFormStore } from '@components/form';
import { useDeleteModal } from '@components/generic-modals';
import { IconButton } from '@components/icon-button';
import { IconFancyContainer } from '@components/icon-fancy-container';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconCondition } from '@components/icons/condition';
import { IconDuplicate } from '@components/icons/duplicate';
import { IconEdit } from '@components/icons/edit';
import { IconFile } from '@components/icons/file';
import { IconInfo } from '@components/icons/info';
import { IconInput } from '@components/icons/input';
import { IconList } from '@components/icons/list';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconPlay } from '@components/icons/play';
import { IconRemove } from '@components/icons/remove';
import { IconSearch } from '@components/icons/search';
import { IconWebhook } from '@components/icons/webhook';
import { RelativeSpinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { MenuItem } from '@components/menu/menu';
import { Pagination, usePagination } from '@components/pagination';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { styledTranslate, useScopedTranslate } from '@locale/translate';
import { Font } from '@styles/theme';
import { abbreviateDate } from '@utils/date';

import {
  useDeleteIntegrationWebhook,
  useIntegrationWebhookLogs,
  useUpdateIntegrationWebhook,
} from 'api/integrations/hooks';
import {
  IntegrationEndpointVariable,
  IntegrationWebhook,
  IntegrationWebhookLogs,
  WebhookCondition,
} from 'api/integrations/requests';
import { getComparatorLabel, getValueLabel } from 'ui/actions/condition-v2/description';

import {
  FieldTable,
  MethodTag,
  getFormEndpointVariables,
  getVariableCount,
  methodFancyContainerColorMap,
} from './common';
import { useSubscribeWebhookModal } from './modals/create-webhook';
import { useDuplicateIntegrationWebhookModal } from './modals/duplicate-webhook';
import { useUnsubscribeWebhookModal } from './modals/remove-webhook';
import { useIntegrationWebhookCreateSidebar } from './sidebars/add-edit-webhook';
import { useIntegrationWebhookConditionsSidebar } from './sidebars/add-webhook-condition';

type WebhookCardProps = {
  webhook: IntegrationWebhook;
};

export const WebhookCard = ({ webhook }: WebhookCardProps) => {
  const { t } = useScopedTranslate(locale);
  const showEditSidebar = useIntegrationWebhookCreateSidebar();
  const deleteMutation = useDeleteIntegrationWebhook(webhook.shortId);
  const showDuplicateModal = useDuplicateIntegrationWebhookModal();
  const showRemoveModal = useDeleteModal();
  const integrationId = webhook.integration;
  return (
    <CollapseCard
      keepCollapsedInDom
      defaultCollapsed
      space={4}
      variant="white"
      key={webhook.shortId}
      renderCollapse={() => (
        <Stack space={2}>
          {webhook.isSubscribable && <Details webhook={webhook} />}
          <PayloadValues webhook={webhook} />
          {webhook.webhookCreated && (
            <>
              <ConditionValues webhook={webhook} />
              <Logs webhook={webhook} />
            </>
          )}
        </Stack>
      )}
    >
      <Stack>
        <Stack orientation="horizontal" align="center" justify="space-between">
          <Stack space={2} orientation="horizontal" align="center">
            <IconFancyContainer
              variant={methodFancyContainerColorMap[webhook.method]}
              icon={<IconWebhook />}
            />
            <div>
              <Text text={webhook.name} font="semibold14" />
              <Text text={webhook.uniqueUrl} font="regular14" color="textHelper" />
            </div>
          </Stack>
          <Stack space={4} orientation="horizontal" align="center">
            <MethodTag method={webhook.method} />
            <Menu buttonProps={{ iconLeft: <IconMoreVertical />, variant: 'ghost' }}>
              <MenuItem
                icon={<IconEdit />}
                label={t('core.edit')}
                onSelect={() => showEditSidebar({ integrationId, webhook })}
              />
              <MenuItem
                icon={<IconDuplicate />}
                label={t('core.duplicate')}
                onSelect={() => showDuplicateModal({ webhook })}
              />
              {(!webhook.isSubscribable || !webhook.webhookCreated) && (
                <MenuItem
                  icon={<IconRemove />}
                  label={t('removeWebhook')}
                  variant="danger"
                  onSelect={() =>
                    showRemoveModal({
                      title: t('removeWebhook'),
                      description: t('removeWebhookDescription'),
                      buttonText: t('removeWebhook'),
                      variant: 'LARGE',
                      onConfirm: () => {
                        deleteMutation.mutateAsync();
                      },
                    })
                  }
                />
              )}
            </Menu>
          </Stack>
        </Stack>
      </Stack>
    </CollapseCard>
  );
};

const Details = ({ webhook }: { webhook: IntegrationWebhook }) => {
  const { t } = useScopedTranslate(locale);
  const subscribeWebhookModal = useSubscribeWebhookModal();

  return (
    <CollapseCard
      keepCollapsedInDom
      defaultCollapsed={webhook.webhookCreated}
      spaceX={4}
      spaceY={2}
      variant="gray"
      renderCollapse={() =>
        webhook.webhookCreated ? (
          <CreatedDetails webhook={webhook} />
        ) : (
          <Stack orientation="horizontal" align="center" justify="space-between">
            <Stack space={2} orientation="horizontal" align="center">
              <IconWebhook color="elementPurple80" />
              <Text text={t('webhookNotSubscribed')} font="semibold14" color="textHelper" />
            </Stack>
            <Button
              stopPropagation
              variant="secondary"
              text={t('subscribe')}
              iconLeft={<IconAddPlus />}
              onClick={() => subscribeWebhookModal({ webhookId: webhook.shortId })}
            />
          </Stack>
        )
      }
    >
      <Stack orientation="horizontal" justify="space-between" align="center">
        <Stack orientation="horizontal" align="center" space={2}>
          <IconInfo color="elementPurple50" />
          <Text text={t('details')} font="semibold14" />
        </Stack>
      </Stack>
    </CollapseCard>
  );
};

const CreatedDetails = ({ webhook }: { webhook: IntegrationWebhook }) => {
  const { t } = useScopedTranslate(locale);
  const unsubscribeModal = useUnsubscribeWebhookModal();
  return (
    <Stack space={4}>
      <Stack space={4} orientation="horizontal">
        <CodeBlockContainer>
          <CodeBlock header={t('requestValues')} data={webhook.requestData} />
        </CodeBlockContainer>
        <CodeBlockContainer>
          <CodeBlock header={t('responseValues')} data={webhook.responseValues} />
        </CodeBlockContainer>
      </Stack>
      <div>
        <Button
          stopPropagation
          variant="secondary"
          text={t('unsubscribeWebhook')}
          iconLeft={<IconRemove />}
          onClick={() => unsubscribeModal({ webhook })}
        />
      </div>
    </Stack>
  );
};

const CodeBlockContainer = styled.div({
  flex: 1,
  overflow: 'auto',
});

type PayloadData = {};

const PayloadForm = createForm<PayloadData>();

const PayloadValues = ({ webhook }: { webhook: IntegrationWebhook }) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();
  const payLoadVariables = webhook.payloadVariables as IntegrationEndpointVariable[];
  const formActionPortalId = `${webhook.shortId}-payload-form-actions-portal`;
  const updateMutation = useUpdateIntegrationWebhook();
  const showEditSidebar = useIntegrationWebhookCreateSidebar();
  const integrationId = webhook.integration;

  const visibleColumns = {
    name: true,
    type: true,
    label: true,
    example: false,
    default: false,
    visible: false,
    format: false,
  };

  const handleUpdateVariables = async () => {
    const variables = getFormEndpointVariables({
      formStore,
      endpointVariables: payLoadVariables,
      visibleColumns,
    });
    await updateMutation.mutateAsync({
      shortId: webhook.shortId,
      data: {
        payloadVariables: variables,
      },
    });
  };

  return (
    <CollapseCard
      keepCollapsedInDom
      defaultCollapsed
      spaceX={4}
      spaceY={2}
      variant="gray"
      renderCollapse={() => (
        <PayloadForm store={formStore} onSubmit={handleUpdateVariables}>
          {payLoadVariables.length > 0 ? (
            <FieldTable
              endpointId={webhook.shortId}
              form={PayloadForm}
              endpointVariables={payLoadVariables}
              visibleColumns={visibleColumns}
            />
          ) : (
            <Stack orientation="horizontal" align="center" justify="space-between">
              <Stack space={2} orientation="horizontal" align="center">
                <IconInput color="elementPurple80" />
                <Text text={t('noPayloadValues')} font="semibold14" color="textSecondary" />
              </Stack>
              <Button
                stopPropagation
                variant="secondary"
                text={t('editPayload')}
                iconLeft={<IconAddPlus />}
                onClick={() => showEditSidebar({ integrationId, webhook })}
              />
            </Stack>
          )}

          {isDirty && (
            <PayloadForm.Actions portal={formActionPortalId}>
              <Stack orientation="horizontal" space={2} align="center">
                <PayloadForm.Reset stopPropagation text={t('core.cancel')} variant="secondary" />
                <PayloadForm.Submit
                  stopPropagation
                  isDisabled={updateMutation.isLoading}
                  text={t('core.save')}
                  variant="primary-blue"
                />
              </Stack>
            </PayloadForm.Actions>
          )}
        </PayloadForm>
      )}
    >
      <Stack orientation="horizontal" justify="space-between" align="center">
        <Stack orientation="horizontal" align="center" space={2}>
          <IconInput color="elementPurple50" />
          <Text text={t('payloadValues')} font="semibold14" />
          <Text text="•" color="textHelper" />
          <Text text={getVariableCount(payLoadVariables)} font="semibold14" />
        </Stack>
        <Stack space={2} orientation="horizontal" align="center">
          <div id={formActionPortalId} />
        </Stack>
      </Stack>
    </CollapseCard>
  );
};

const ConditionValues = ({ webhook }: { webhook: IntegrationWebhook }) => {
  const { t } = useScopedTranslate(locale);
  const conditions = webhook.conditions ?? [];
  const conditionSidebar = useIntegrationWebhookConditionsSidebar();

  const comparatorElements = conditions.map((condition: WebhookCondition, index: number) => {
    const { field, comparator, value } = condition;
    const valueLabel = getValueLabel(value as string, comparator);
    const fieldLabel = field?.variables && Object.values(field.variables)[0].label;
    if (!fieldLabel || !comparator) {
      return '';
    }
    const comparatorString = getComparatorLabel(comparator) || comparator;
    const commonProps = { font: 'regular14' as Font, style: { display: 'inline' } };
    const emptyValueProps = !valueLabel ? { font: 'semibold14' as Font } : {};
    const conjunction = t('core.and');
    return (
      <div key={index}>
        {styledTranslate({
          key: 'conditionDescription',
          variables: {
            conjunction: {
              text: index > 0 ? `${conjunction} ` : '',
              color: 'textSecondary',
              capitalize: true,
              ...commonProps,
            },
            field: {
              text: fieldLabel,
              color: 'elementBlue100',
              ...commonProps,
            },
            comparator: {
              text: comparatorString,
              color: 'textSecondary',
              ...commonProps,
            },
            value: {
              text: valueLabel || t('filledOutInTheJourney'),
              color: 'textSecondary',
              ...commonProps,
              ...emptyValueProps,
            },
          },
          translations: locale,
        })}
      </div>
    );
  });

  return (
    <CollapseCard
      keepCollapsedInDom
      defaultCollapsed
      spaceX={4}
      spaceY={2}
      variant="gray"
      renderCollapse={() => (
        <>
          {conditions.length === 0 ? (
            <Stack orientation="horizontal" align="center" justify="space-between">
              <Stack space={2} orientation="horizontal" align="center">
                <IconCondition color="elementPurple80" />
                <Text text={t('noConditions')} font="semibold14" color="textSecondary" />
              </Stack>
              <Button
                stopPropagation
                variant="secondary"
                text={t('addConditions')}
                iconLeft={<IconAddPlus />}
                onClick={() => conditionSidebar({ webhookId: webhook.shortId })}
              />
            </Stack>
          ) : (
            <Stack space={4}>
              {comparatorElements}
              <div>
                <Button
                  stopPropagation
                  variant="secondary"
                  text={t('updateConditions')}
                  iconLeft={<IconCondition />}
                  onClick={() => conditionSidebar({ webhookId: webhook.shortId })}
                />
              </div>
            </Stack>
          )}
        </>
      )}
    >
      <Stack orientation="horizontal" justify="space-between" align="center">
        <Stack space={2} orientation="horizontal" align="center">
          <IconPlay color="elementPurple50" />
          <Text text={t('continueWhen')} font="semibold14" />
          <Text text="•" color="textHelper" />
          <Text text={conditions.length} font="semibold14" />
        </Stack>
      </Stack>
    </CollapseCard>
  );
};

const LOGS_PER_PAGE = 10;
const LogsTable = ({ logs }: { logs: IntegrationWebhookLogs[] }) => {
  const [selectedLog, setSelectedLog] = useState<IntegrationWebhookLogs>();
  const { numPages, selectedPage, selectPage, getItemsForPage } = usePagination({
    pageSize: LOGS_PER_PAGE,
    defaultPage: 1,
    count: logs.length,
  });

  const logsList = getItemsForPage(logs) as IntegrationWebhookLogs[];

  return (
    <Stack space={4} orientation="horizontal" justify="space-between">
      <CodeBlockContainer>
        <Stack space={4}>
          <Stack space={2}>
            {logsList.map((log) => (
              <Stack space={2} orientation="horizontal" key={log.shortId} align="center">
                <IconButton icon={<IconSearch />} onClick={() => setSelectedLog(log)} />
                <Text
                  font={selectedLog?.shortId === log.shortId ? 'semibold14' : 'regular14'}
                  text={abbreviateDate(log.dateCreated, 'en', 'datetime-seconds')}
                />
              </Stack>
            ))}
          </Stack>
          <Stack orientation="horizontal" justify="flex-start">
            <Pagination
              justify="right"
              count={numPages}
              selected={selectedPage}
              onSelect={selectPage}
            />
          </Stack>
        </Stack>
      </CodeBlockContainer>
      {selectedLog ? (
        <CodeBlockContainer>
          <CodeBlock
            header={abbreviateDate(selectedLog.dateCreated, 'en', 'datetime-seconds')}
            data={selectedLog.requestData}
          />
        </CodeBlockContainer>
      ) : (
        <></>
      )}
    </Stack>
  );
};

const Logs = ({ webhook }: { webhook: IntegrationWebhook }) => {
  const { t } = useScopedTranslate(locale);
  const { data, isLoading } = useIntegrationWebhookLogs(webhook.shortId);
  return (
    <CollapseCard
      keepCollapsedInDom
      defaultCollapsed
      spaceX={4}
      spaceY={2}
      variant="gray"
      renderCollapse={() => (
        <>
          {isLoading || !data ? (
            <RelativeSpinner />
          ) : data.length === 0 ? (
            <Stack space={2} orientation="horizontal" align="center">
              <IconFile color="elementPurple80" />
              <Text text={t('noLogsFound')} font="semibold14" color="textSecondary" />
            </Stack>
          ) : (
            <LogsTable logs={data} />
          )}
        </>
      )}
    >
      <Stack orientation="horizontal" justify="space-between" align="center">
        <Stack space={4} orientation="horizontal" align="center">
          <IconList color="elementPurple50" />
          <Text text={t('logs')} font="semibold14" />
        </Stack>
      </Stack>
    </CollapseCard>
  );
};

const locale = {
  en: {
    details: 'Details',
    webhookNotSubscribed: 'Not subscribed to a webhook',
    subscribe: 'Subscribe',
    requestValues: 'Request values',
    payloadValues: 'Payload values',
    responseValues: 'Response values',
    continueWhen: 'Continue when',
    unsubscribeWebhook: 'Unsubscribe webhook',
    noConditions: 'No conditions found',
    addConditions: 'Add conditions',
    conditionDescription: '{conjunction}{field} {comparator} {value}',
    continueIfDescription: 'Continue if\n{conditions}',
    filledOutInTheJourney: 'Value selected in journey builder',
    updateConditions: 'Update conditions',
    removeWebhook: 'Delete webhook',
    removeWebhookDescription:
      'You are about to permanently delete this webhook. The following will be affected:\n• All current configurations and response values\n• Journeys that rely on this endpoint\n• Travellers who are currently in progress\n\nOnce deleted, you can recreate this endpoint, but any lost data or configurations cannot be restored.',
    logs: 'Logs',
    noPayloadValues: 'No payload values found',
    editPayload: 'Edit payload values',
    noLogsFound: 'No logs found',
  },
};
